import React, { ChangeEvent, FunctionComponent } from 'react';

import classnames from 'classnames';
import { isFunction, isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';

import { RatingInputProps } from 'core/rating/types';
import {
    DEFAULT_COLOR_EMPTY_BY_VALUE,
    DEFAULT_COLOR_FULL_BY_VALUE,
    parseHtmlName,
    parseIconEmpty,
    parseIconFull,
    parseIncrement,
    parseIsReadOnly,
    parseSize,
    parseTabIndex,
} from 'core/rating/util/Props';
import { getPotentialIncrementedValues } from 'core/rating/util/Values';
import { generateInputWidthPctg, generateValueNotationWidthPctg } from 'core/rating/util/Width';

import 'core/rating/rating-inputs/rating-input/RatingInput.scss';

export const RatingInput: FunctionComponent<RatingInputProps> = ({
    className,
    colorEmptyByValue,
    colorFullByValue,
    hoverValue,
    htmlName: htmlNameFromProps,
    iconEmpty: iconEmptyFromProps,
    iconFull: iconFullFromProps,
    increment: incrementFromProps,
    isReadOnly: isReadOnlyFromProps,
    onChange: onChangeFromProps,
    selectedValue,
    size: sizeFromProps,
    tabIndex: tabIndexFromProps,
    value,
}) => {
    const { t } = useTranslation('core/rating');
    const rootClassName = 'r4e-rating-input';
    const htmlName = parseHtmlName(htmlNameFromProps);
    const iconEmpty = parseIconEmpty(iconEmptyFromProps);
    const iconFull = parseIconFull(iconFullFromProps);
    const increment = parseIncrement(incrementFromProps);
    const incrementedValues = getPotentialIncrementedValues(value, increment);
    const inputWidth = generateInputWidthPctg(increment);
    const isReadOnly = parseIsReadOnly(isReadOnlyFromProps);
    const size = parseSize(sizeFromProps);
    const tabIndex = parseTabIndex(tabIndexFromProps, isReadOnly);
    const hoverValueNotationWidth = generateValueNotationWidthPctg(incrementedValues, hoverValue);
    const valueNotationWidth = generateValueNotationWidthPctg(incrementedValues, selectedValue);
    const onChange = isReadOnly
        ? undefined
        : (event: ChangeEvent<HTMLInputElement>): void => {
              if (isFunction(onChangeFromProps)) {
                  const value = Number(event.target.value);

                  onChangeFromProps(value);
              }
          };

    return (
        <div
            className={classnames(`${rootClassName} size-${size}`, className, {
                'state-read-only': isReadOnly,
            })}
        >
            {!isReadOnly &&
                incrementedValues.map((incrementedValue, index) => (
                    <div
                        key={incrementedValue}
                        className="input-wrapper"
                        title={t('STAR_RATING', { count: incrementedValue })}
                        style={{
                            left: `${(index * 100) / incrementedValues.length}%`,
                            width: inputWidth,
                        }}
                    >
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label>
                            <input
                                defaultChecked={selectedValue === incrementedValue}
                                disabled={isReadOnly}
                                name={htmlName}
                                onChange={onChange}
                                tabIndex={tabIndex}
                                type="radio"
                                value={incrementedValue}
                            />
                            <span className="display-label">
                                <span className="sr-only">{t('STAR_RATING', { count: value })}</span>
                            </span>
                        </label>
                    </div>
                ))}
            <span className="value-notation">
                <span
                    className="value-state value-state-empty"
                    style={{
                        color: colorEmptyByValue[value],
                    }}
                >
                    <i className={iconEmpty} />
                </span>
                <span
                    className={classnames('value-state value-state-filled', {
                        'value-state-filled-on-hover':
                            !isReadOnly &&
                            hoverValue &&
                            (hoverValue >= value || incrementedValues.includes(hoverValue)),
                    })}
                    style={{
                        color: colorFullByValue[value],
                        width: isUndefined(hoverValue) ? valueNotationWidth : hoverValueNotationWidth,
                    }}
                >
                    <i className={iconFull} />
                </span>
            </span>
        </div>
    );
};

RatingInput.defaultProps = {
    className: undefined,
    colorEmptyByValue: DEFAULT_COLOR_EMPTY_BY_VALUE,
    colorFullByValue: DEFAULT_COLOR_FULL_BY_VALUE,
    onChange: undefined,
    htmlName: parseHtmlName(),
    iconEmpty: parseIconEmpty(),
    iconFull: parseIconFull(),
    increment: parseIncrement(),
    isReadOnly: parseIsReadOnly(),
    selectedValue: undefined,
    size: parseSize(),
    tabIndex: parseTabIndex(),
};
